/**
 * PageTransition Component
 * 
 * Provides minimal, iOS-style page transitions:
 * - Simple, consistent animations across the app
 * - Hardware-accelerated for smooth performance
 * - Just two transition types: horizontal and vertical
 * - Respects user's reduced motion preferences
 * - Prevents animation conflicts
 * 
 * @version 2.3
 */

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import usePageTransition from '../../utils/usePageTransition';
import './PageTransition.css';

/**
 * PageTransition Component 
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render with transition
 * @returns {JSX.Element} Children wrapped in transition effects
 */
const PageTransition = ({ children }) => {
  // Get current location for transition key
  const location = useLocation();
  
  // Use the enhanced hook that provides type and animation state
  const { type, isAnimating } = usePageTransition();
  
  // Transition duration and timeout values
  const duration = 300;
  
  // Reference for transition target
  const nodeRef = React.useRef(null);
  
  // Determine class names based on transition type
  const classNames = type === 'vertical' ? 'page-transition-vertical' : 'page-transition-horizontal';

  // Reset all animations when changing routes
  useEffect(() => {
    // Add a class to the document body to pause all animations during transitions
    if (isAnimating) {
      document.body.classList.add('paused-animations');
    } else {
      document.body.classList.remove('paused-animations');
    }

    return () => {
      document.body.classList.remove('paused-animations');
    };
  }, [isAnimating]);

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition
        key={location.key}
        timeout={duration}
        classNames={classNames}
        nodeRef={nodeRef}
        style={{ '--transition-duration': `${duration}ms` }}
        onEnter={() => {
          // Reset any previous animation state
          if (nodeRef.current) {
            nodeRef.current.style.animation = 'none';
            void nodeRef.current.offsetHeight; // Force reflow - using void to indicate intentional side effect
            nodeRef.current.style.animation = null;
          }
        }}
      >
        <div 
          ref={nodeRef} 
          className={`page-transition ${isAnimating ? 'no-child-animations' : ''}`}
        >
          {children}
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
};

export default PageTransition; 